import { useAuthStore, AUTH_ERROR_CODES, AUTH_ERROR_MESSAGES } from "../stores/authStore";
import { supabase } from "./supabase";

const SESSION_ERROR_PATTERN = /invalid token|session expired/i;

/**
 * Detects if a packages-query response/exception indicates an expired session.
 */
function isSessionExpired(error: unknown): boolean {
  const status =
    (error as { status?: number })?.status ??
    (error as { context?: { response?: { status?: number } } })?.context?.response?.status;

  const message =
    typeof error === "string"
      ? error
      : typeof (error as { message?: string })?.message === "string"
        ? (error as { message: string }).message
        : "";

  const matchesMessage = message ? SESSION_ERROR_PATTERN.test(message) : false;

  return status === 401 || matchesMessage;
}

/**
 * Handles auth/session errors by logging the user out with a consistent message.
 * Returns true if the error was handled as a session expiry.
 */
function handleSessionExpiry(error: unknown): boolean {
  if (!isSessionExpired(error)) return false;

  // Logout will also surface the UX message stored in AUTH_ERROR_MESSAGES
  const logout = useAuthStore.getState().logout;
  logout(AUTH_ERROR_CODES.TOKEN_EXPIRED);
  return true;
}

/**
 * Standardises packages-query responses, handling session expiry automatically.
 */
export function ensurePackagesSuccess<T>(data: any, error: any): T {
  if (error) {
    if (handleSessionExpiry(error)) {
      throw new Error(AUTH_ERROR_MESSAGES[AUTH_ERROR_CODES.TOKEN_EXPIRED]);
    }
    throw new Error(error.message || "Errore durante la richiesta");
  }

  if (!data?.success) {
    if (handleSessionExpiry(data?.error)) {
      throw new Error(AUTH_ERROR_MESSAGES[AUTH_ERROR_CODES.TOKEN_EXPIRED]);
    }
    throw new Error(data?.error || "Query error");
  }

  return data.data as T;
}

/**
 * Protected API call to packages-query Edge Function with params and optional AbortSignal
 */
export async function queryPackagesProtected<T>(
  token: string | null,
  action: string,
  params: Record<string, unknown> = {},
  signal?: AbortSignal
): Promise<T> {
  if (!token) {
    throw new Error("Not authenticated");
  }

  const { data, error } = await supabase.functions.invoke("packages-query", {
    body: { action, params },
    headers: {
      "X-Portale-Token": token,
    },
    signal, // Pass AbortSignal to cancel the request if needed
  });

  return ensurePackagesSuccess<T>(data, error);
}


